/**
 * Draw coating for room
 *
 * Used to draw a polygone form on a wall which will be filled with a coating inside
 */

import { AreaState, AreaStateEditModeEnum, ActionState, ActionStateEnum, IdleDecoration } from "..";
import { Command } from "../../../../constants";
import { PlanManager } from "../../../../cocosPlan";
import { Area, Coating, Commands, ComponentConstants, Entity, Events, eventsManager, Floor, FloorCoatingArea, math, roomManager, SavaneConstants, SavaneMath } from "@rhinov/savane-js";

declare var cc: any;
declare var $: any;

export class FloorCoatingAreaState extends AreaState {

    _originalCursorPosition: math.vec3 | null = null;
    _openTulip: boolean = true;
    _coating: any;
    declare _area: FloorCoatingArea;

    constructor(coating: Area | FloorCoatingArea, editMode: number | null, cursorPosition: math.vec3) {
        // Store edition mode (create or edit)
        super(editMode ? coating as Area : null, cursorPosition, editMode ? null: coating);

        // Record cursor position
        if (cursorPosition) {
            this._originalCursorPosition = math.vec3.clone(cursorPosition);
        }
        // Open tulip at the end
        

        // If edit
        if (!editMode) {
            // Store the coating used to create the area
            this._coating = coating;
        }

        // Set current state enum
        this.state = ActionStateEnum.FloorCoatingArea;
    }

    getAction(): number {
        return ActionStateEnum.FloorCoatingArea;
    }

    // Go to next state (i.e. go back to idle decoration state)
    nextState(): ActionState | null {
        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.idleDecoration);
        return new IdleDecoration();
    }

    isDecorationState(): boolean {
        return (true);
    }

    // Create a command to delete the current area (so it can be undone)
    deleteCommand(): Command {
        return new Commands.DeleteComponentCommand(this._area, true);
    }

    // Create a command to create a new area (so it can be undone)
    addCommand(): Command {
        switch(this._area.hangType) {
            case Coating.HangType.ceiling:
                var room = roomManager.getRoomAtPosition(this._area.vertices[0], this._entity as Floor);
                if (room) {
                    this._area.altitude = room.height;
                }
                for (var i = 1 ; i < this._area.vertices.length ; i++) {
                    room = roomManager.getRoomAtPosition(this._area.vertices[i], this._entity as Floor);
                    if (room) {
                        if (this._area.altitude > room.height) {
                            this._area.altitude = room.height;
                        }
                    }
                }
                break;

            case Coating.HangType.floor:
                var room = roomManager.getRoomAtPosition(this._area.vertices[0], this._entity as Floor);
                if (room) {
                    this._area.altitude = room.floorHeight;
                }
                for (var i = 1 ; i < this._area.vertices.length ; i++) {
                    room = roomManager.getRoomAtPosition(this._area.vertices[i], this._entity as Floor);
                    if (room) {
                        if (this._area.altitude < room.floorHeight) {
                            this._area.altitude = room.floorHeight;
                        }
                    }
                }
                break;
        }
        return new Commands.AddComponentCommand(this._area, this._entity!, false, true);
    }

    _isValid() {
        return true;
    }

    // State initialisation
    init(area: FloorCoatingArea, cursorPosition: math.vec3, entity: Entity, doubleClick?: boolean, coating?: any) {

        if (coating) {
            this._coating = coating;
        }

        // Call area state initialisation (it will create an area but we wil overwrite it below)
        super.init(area, cursorPosition, undefined, undefined);

        // If we are creating a new coating area
        if (this.editMode === AreaStateEditModeEnum.new_area) {
            // Let's try to load a diffuse media from the coating within the asset manager (it might be missing)
            var urlPreview = this._coating.url + this._coating._id + '/medias/maps/diffuse' + this._coating.choosenIndex + '.jpg';

            urlPreview = urlPreview.replace("assets", "coatings");

            var hangType = (this._coating.hang.floor ? Coating.HangType.floor : Coating.HangType.ceiling);
            // Create a coating area for the area (and not simply an normal area as we need a coating info in addition to the standard fields of area object)
            if (this._coating.colors !== undefined) {
                // New structure with realColor at the root of the AM coating
                this._area = new FloorCoatingArea(this._coating._id, this._coating.manufacturer, this._coating.retailer, hangType, [this._coating.colors], this._coating.randomization, this._coating.isHpFloorGenerator ? this._coating.hpFloorGeneratorSettings : undefined, urlPreview);
            }
            else {
                // Old structure with colors in configs
                this._area = new FloorCoatingArea(this._coating._id, this._coating.manufacturer, this._coating.retailer, hangType, this._coating.configs, this._coating.randomization, this._coating.isHpFloorGenerator ? this._coating.hpFloorGeneratorSettings : undefined, urlPreview);
            }
            // Store the entity owning the coating area
            this._area.entity = this._entity;
            // Store the desired color index for the coating
            //this._area.colorIndex = this._coating.colorIndex !== undefined ? this._coating.colorIndex : this._coating.configs[0].index;
            // And if we have a cursorPosition pass to this method, store it as the first point of the area
            if (cursorPosition) {
                this._area.vertices = [cursorPosition];
            }
            else {
                this._area.vertices = [];
            }
        }
    }

    // Mouse button pressEd
    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        eventsManager.instance.dispatch(Events.CANCEL_FLOOR_GENERATOR_CHANGED);
        // Call the AreaState method and store result (where to go)
        var state = super.mouseClickDown(cursorPosition, doubleClick, hoverEntities, mouseButton, true);
        switch (this.editMode) {
            case AreaStateEditModeEnum.area_selected:
            case AreaStateEditModeEnum.point_selected:
            case AreaStateEditModeEnum.line_selected:
                this._isEditing = true;
                break;
        }

        // Close any tulip here
        PlanManager.getInstance().updateItemsSelected(null, this._isEditing, false);

        // If edit mode is unknown then it means we have clicked outside of the current coating area
        if (this.editMode === AreaStateEditModeEnum.unknown) {
            // Current area isn't edited anymore, check all coating area
            var areaCoatings = this._entity!.getComponents(ComponentConstants.ComponentType.FloorCoatingArea) as FloorCoatingArea[];

            // Parse them all
            for (var i = 0; i < areaCoatings.length; ++i) {
                // If one found with current direct side and the cursor position is in the polygon
                if (SavaneMath.isInPoly(cursorPosition, areaCoatings[i].vertices)) {
                    // Create a new view coating state with the new edited coating
                    return new FloorCoatingAreaState(areaCoatings[i], Number(true), cursorPosition);
                }
            }

            // No new coating area selected, go back to idle decoration
            return (this.nextState());
        }
        else {
            // Return result from Area State call
            return (state);
        }
    }

    // The mouse button is released
    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        if (this._coating && this._area.vertices.length === 1) { // drag over area -> replace coating
            var areaCoatings = this._entity!.getComponents(ComponentConstants.ComponentType.FloorCoatingArea) as FloorCoatingArea[];

            for (var i = 0; i < areaCoatings.length; ++i) {
                if (SavaneMath.isInPoly(cursorPosition, areaCoatings[i].vertices)) {
                    var area = areaCoatings[i];
                    this._area.vertices = area.vertices.slice();
                    if (area.floorGeneratorSettings) {
                        eventsManager.instance.dispatch(Events.SHOW_CONTEXTUAL_MENU_COATING_AREA, {
                            jointCallback: area.floorGeneratorSettings.cleat ? null : () => {
                                if (this._coating.isHpFloorGenerator) {
                                    eventsManager.instance.dispatch(Events.DISPLAY_ALERT, { message: 'Les matériaux FloorGenerator sont interdits sur les joints.' });
                                } else {
                                    (area as any).floorGeneratorSettings.jointCoating = {
                                        id: this._coating._id,
                                        colors: (this._coating.colors !== undefined ? [this._coating.colors] : this._coating.configs)
                                    }
                                    PlanManager.getInstance().saveInCache();
                                    eventsManager.instance.dispatch(Events.FRONTVIEW_FLOOR_GENERATOR_CHANGED);
                                }
                                ($('#ContextualMenuCoatingArea') as any).css('display', 'none');
                            },
                            cleatCallback: area.floorGeneratorSettings.cleat ? () => {
                                (this._area as any).floorGeneratorSettings = (area as any).floorGeneratorSettings;
                                (this._area as any).floorGeneratorSettings.tile.texturesCount = 1;
                                (this._area as any).rotation = (area as any).rotation;
                                (this._area as any).altitude = (area as any).altitude;
                                PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(area, this._area, this._entity!));
                                eventsManager.instance.dispatch(Events.FRONTVIEW_FLOOR_GENERATOR_CHANGED);
                                ($('#ContextualMenuCoatingArea') as any).css('display', 'none');
                            } : null,
                            replaceCallback: () => {
                                (this._area as any).altitude = (area as any).altitude;
                                PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(area, this._area, this._entity!));
                                ($('#ContextualMenuCoatingArea') as any).css('display', 'none');
                            },
                        });
                        return new FloorCoatingAreaState(this._area, Number(true), cursorPosition);
                    } else {
                        (this._area as any).altitude = (area as any).altitude;
                        PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(area, this._area, this._entity!));
                        return new FloorCoatingAreaState(this._area, Number(true), cursorPosition);
                    }
                }
            }
        }

        if (this.editMode === AreaStateEditModeEnum.area_selected && this._isEditing === true && this._openTulip === false) {
            this._updateWebGlDynamicHull();
        }

        // Call area state method with the cursor position and store the result
        var state = super.mouseClickUp(cursorPosition, elapsedTime, hoverEntities, mouseButton);

        if (state === null) {
            if (SavaneMath.isInPoly(cursorPosition, this._area.vertices)) {
                if (this._openTulip && this._entity) {
                    PlanManager.getInstance().updateItemsSelected([this._entity], true, true);
                }
            } else if (this.editMode && !this._isEditing) {
                super.cancelAllActions();
                return this.nextState();
            }
        }

        // Go to the result of the area state call
        return state;
    }

    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        // Do we have to open the tulip at the end of the current operation ?
        if (!this._originalCursorPosition) {
            this._originalCursorPosition = math.vec3.clone(cursorPosition);
        }
        if (math.vec3.distance(this._originalCursorPosition, cursorPosition) > SavaneConstants.PositionTolerance) {
            this._openTulip = false;
        }

        // Call AreaState method to know what to do and store result
        var toReturn = super.moveMouseCursor(cursorPosition);

        // Compute if the shape is valid
        this._isValid();
        // Return result of the call above
        return (toReturn);
    }

    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        if ((keyCode === cc.KEY.Delete || keyCode == cc.KEY.backspace)) {
            this.editMode = AreaStateEditModeEnum.unknown;
        }
        var nextState = super.keyPressed(keyCode);

        if (keyCode === cc.KEY.s) {
            PlanManager.getInstance().snapEnabled = !PlanManager.getInstance().snapEnabled;
            eventsManager.instance.dispatch(Events.SNAP_UPDATED);
        }

        if (keyCode === cc.KEY.a) {
            PlanManager.getInstance().magnetRotation = !PlanManager.getInstance().magnetRotation;
            eventsManager.instance.dispatch(Events.ANGLE_SNAP_UPDATED);
        }

        if (keyCode === cc.KEY.c) {
            return this._makeCircle();
        }

        if (keyCode === cc.KEY.e) {
            return this._makeElipse();
        }

        return nextState;
    }

    _makeCircle(): ActionState {
        if (this._area.vertices.length < 3) {
            return null as any;
        }

        var center = math.vec3.create();
        math.vec3.add(center, this._area.vertices[0], this._area.vertices[1]);
        math.vec3.divide(center, center, math.vec3.fromValues(2, 2, 2));
        var radius = math.vec3.distance(this._area.vertices[0], this._area.vertices[1]) / 2;
        var tanStep = 60;
        var angleStep = Math.atan(tanStep / radius);
        var newVertices = [];
        for (var step = 0; step < 2 * Math.PI; step += angleStep) {
            newVertices.push(math.vec3.fromValues(center[0] + radius * Math.cos(step), center[1] + radius * Math.sin(step), center[2]));
        }

        var newArea = this._area.clone();
        newArea.vertices = newVertices;
        PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(this._area, newArea, this._entity!));
        return this.nextState()!;
    }

    _makeElipse(): ActionState {
        if (this._area.vertices.length < 3) {
            return null as any;
        }

        var center = math.vec3.create();
        math.vec3.add(center, this._area.vertices[0], this._area.vertices[1]);
        math.vec3.divide(center, center, math.vec3.fromValues(2, 2, 2));
        var a = math.vec3.distance(this._area.vertices[0], this._area.vertices[1]) / 2;
        var b = math.vec3.distance(this._area.vertices[0], this._area.vertices[2]);
        var x = math.vec3.create();
        math.vec3.subtract(x, this._area.vertices[1], this._area.vertices[0]);
        math.vec3.normalize(x, x);
        var y = math.vec3.create();
        math.vec3.subtract(y, this._area.vertices[2], this._area.vertices[0]);
        math.vec3.normalize(y, y);
        var z = math.vec3.create();
        math.vec3.cross(z, y, x);
        math.vec3.cross(y, x, z);
        var frame = math.mat4.fromValues(
            x[0], x[1], x[2], 0,
            y[0], y[1], y[2], 0,
            z[0], z[1], z[2], 0,
            center[0], center[1], center[2], 1);
        var tanStep = 60;
        var angleStep = Math.atan(tanStep / a);
        var newVertices = [];
        for (var step = 0; step < 2 * Math.PI; step += angleStep) {
            var position = math.vec3.fromValues(a * Math.cos(step), b * Math.sin(step), 0);
            math.vec3.transformMat4(position, position, frame);
            newVertices.push(position);
        }

        var newArea = this._area.clone();
        newArea.vertices = newVertices;
        PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(this._area, newArea, this._entity!));
        return this.nextState()!;
    }

    _updateWebGlDynamicHull() {
        // Inform WebGL to update the dynamic hull
        eventsManager.instance.dispatch(Events.COMMAND_EXECUTED, {
            name: Commands.CommandEnum.EditComponentCommand,
            undo: false,
            datas: {
                component: this._area
            }
        });
    }
}